import { useState, useMemo, useEffect } from "react";
import { makeRequest } from "app/utils/makeRequest";
import { getSessionStore } from "app/utils/sessionStore";
import { getObjectFromLocalStore } from "app/utils/localStore";
import { THEME, USERTYPE } from "app/utils/types";
import { useIPContext } from "app/pages/InteractiveProposal/InteractiveProposalProvider";
import { ProgressSpinner } from "primereact/progressspinner";

// ── Types ─────────────────────────────────────────────────────────────────────

interface ApiAdvisor {
  agentId: string;
  profile: { firstName: string; lastName: string; profilePicture: string | null };
  confirmed: boolean | null;
  status: boolean;
  confirmedDate?: string | null;
}

interface ApiInsured {
  policyReviewId: string;
  pfPolicyId: string;
  insuredAge: number;
  insured: {
    id: string;
    email: string;
    profile: { firstName: string; lastName: string; profilePicture: string | null };
  };
  confirmed: boolean | null;
  confirmedDate?: string | null;
  status: boolean;
}

interface ApiSignatureData {
  advisor?: ApiAdvisor;
  insureds?: ApiInsured[];
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const ACCENT = "#1E3A8A";

const formatSignedAt = (iso: string): string => {
  const d = new Date(iso);
  return (
    // eslint-disable-next-line
    d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) +
    " @ " +
    d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true })
  );
};

// ── Icon ──────────────────────────────────────────────────────────────────────

const CheckCircle = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
    <circle cx="8" cy="8" r="7.25" stroke="#22C55E" strokeWidth="1.2" fill="none" />
    <path
      d="M5 8L7 10.5L11 6"
      stroke="#22C55E"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// ── Pill variants ─────────────────────────────────────────────────────────────

const SignedPill = ({ name }: { name: string }) => (
  <div
    className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border"
    style={{ borderColor: "#22C55E", color: "#15803D" }}
  >
    <span className="text-[13px] font-semibold whitespace-nowrap">{name}</span>
    <CheckCircle />
  </div>
);

const PendingPill = ({ name, showArrows = false }: { name: string; showArrows?: boolean }) => (
  <div
    className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border border-gray-300"
    style={{ color: "#374151" }}
  >
    <span className="text-[13px] font-semibold whitespace-nowrap">{name}</span>
    {showArrows && <span className="text-gray-400 text-[11px] font-bold leading-none">»</span>}
  </div>
);

const Timestamp = ({ value }: { value: string }) => (
  <p className="text-[11px] text-gray-400 mt-1.5 text-center">{value}</p>
);

const SignerColumn = ({
  title,
  children,
  dividerColor,
  hasBorderRight = false,
}: {
  title: string;
  children: React.ReactNode;
  dividerColor?: string;
  hasBorderRight?: boolean;
}) => (
  <div
    className="flex flex-col items-center px-6"
    style={hasBorderRight ? { borderRight: `1px solid ${dividerColor ?? "#E5E7EB"}` } : undefined}
  >
    <p className="text-[14px] font-bold mb-4 conthrax-font" style={{ color: ACCENT }}>
      {title}
    </p>
    {children}
  </div>
);

function Signature() {
  const isDark = getSessionStore("globalTheme") === THEME.DARK;

  const userAccess = getObjectFromLocalStore("useraccess");
  const userType: USERTYPE = userAccess?.userType;
  const isAdvisor = userType === USERTYPE.AGENT;

  const { summaryData } = useIPContext();

  const [sigData, setSigData] = useState<ApiSignatureData | null>(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const planId = getSessionStore("planInfo")?.planId ?? "863f01ec-1627-4665-aaf8-fcb8d5e0375b";
  const selectedYear = getSessionStore("ircSelectedYear") ?? 1;
  const creditType = getSessionStore("ircSelectedCreditType") ?? "preCredit";

  const fetchSignatures = () => {
    setLoading(true);
    makeRequest("interactiveReviewCenterSignature")
      .get(`${planId}?year=${selectedYear}&creditType=${creditType}`, true)
      .then((res: any) => setSigData(res?.data ?? null))
      .catch((err: any) => console.error("Failed to fetch signature data:", err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchSignatures();
  }, [planId, selectedYear, creditType]);

  const primaryContactName = useMemo(() => {
    const pc = summaryData?.primaryContact;
    if (!pc) return "";
    return `${pc.firstName ?? ""} ${pc.lastName ?? ""}`.trim();
  }, [summaryData]);

  const advisorName = useMemo(() => {
    if (sigData?.advisor?.profile) {
      const { firstName, lastName } = sigData.advisor.profile;
      return `${firstName ?? ""} ${lastName ?? ""}`.trim();
    }
    const adv = summaryData?.contactAdvisors?.find((a: any) => a?.agentType === "primary");
    if (!adv) return "";
    const profile = adv?.profile ?? adv?.user?.profile ?? adv;
    return `${profile?.firstName ?? ""} ${profile?.lastName ?? ""}`.trim();
  }, [sigData, summaryData]);

  // ── Action handlers ────────────────────────────────────────────────────────

  const handleConfirm = () => {
    setActionLoading(true);
    makeRequest("interactiveReviewCenterConfirmSignature")
      .post({ planId, year: selectedYear, creditType })
      .then(() => fetchSignatures())
      .catch((err: any) => console.error("Confirm failed:", err))
      .finally(() => setActionLoading(false));
  };

  const handleSkip = () => {
    setActionLoading(true);
    makeRequest("interactiveReviewCenterSkipSignature")
      .post({ planId, year: selectedYear, creditType })
      .then(() => fetchSignatures())
      .catch((err: any) => console.error("Skip failed:", err))
      .finally(() => setActionLoading(false));
  };

  const cardBg = isDark ? "bg-[#2A2A3D]" : "bg-white";
  const divider = isDark ? "border-[#3A3A50]" : "border-gray-200";
  const dividerColor = isDark ? "#3A3A50" : "#E5E7EB";
  const textDesc = isDark ? "text-gray-300" : "text-gray-500";

  return (
    <div className={`rounded-xl border overflow-hidden ${cardBg} ${divider} border-solid`}>
      {/* Header */}
      <div className="px-5 pt-4 pb-4">
        <p className="text-[15px] text-[#55556D] font-semibold conthrax-font mb-2">Signature</p>
        <p className={`text-[13px] leading-relaxed ${textDesc}`}>
          I have reviewed this information and confirm that it accurately reflects the discussion
          and understanding at the time of review. This confirmation is for planning purposes only
          and does not authorize any changes to the policy.
        </p>
      </div>

      <div className={`border-t ${divider}`} />

      {/* Signer columns */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <ProgressSpinner style={{ width: 32, height: 32 }} />
        </div>
      ) : (
        <div className="grid grid-cols-3 py-7">
          {/* Primary Contact */}
          <SignerColumn title="Primary Contact" hasBorderRight dividerColor={dividerColor}>
            <PendingPill name={primaryContactName} />
          </SignerColumn>

          {/* Advisor */}
          <SignerColumn title="Advisor" hasBorderRight dividerColor={dividerColor}>
            {sigData?.advisor?.confirmed === true ? (
              <>
                <SignedPill name={advisorName} />
                {sigData.advisor.confirmedDate && (
                  <Timestamp value={formatSignedAt(sigData.advisor.confirmedDate)} />
                )}
              </>
            ) : sigData?.advisor?.confirmed === false ? (
              <>
                <PendingPill name={advisorName} showArrows />
                {sigData.advisor.confirmedDate && (
                  <Timestamp value={formatSignedAt(sigData.advisor.confirmedDate)} />
                )}
              </>
            ) : (
              <>
                <PendingPill name={advisorName} />
                {isAdvisor && (
                  <div className="flex items-center gap-2 mt-3">
                    <button
                      type="button"
                      onClick={handleConfirm}
                      disabled={actionLoading}
                      className="flex gap-1 px-3 py-2 rounded-full text-[12px] text-[#22C55E] font-semibold border border-[#22C55E]"
                      style={{ background: "#CAF1D8" }}
                    >
                      Confirm
                    </button>
                    <button
                      type="button"
                      onClick={handleSkip}
                      disabled={actionLoading}
                      className="px-3 py-2  rounded-full text-[12px] font-semibold text-gray-600 bg-gray-200 transition-opacity disabled:opacity-60"
                    >
                      Skip
                    </button>
                  </div>
                )}
              </>
            )}
          </SignerColumn>

          {/* Insured(s) */}
          <SignerColumn title="Insured(s)">
            <div className="flex flex-col items-center gap-4 w-full">
              {(sigData?.insureds ?? []).map((item) => {
                const name = `${item.insured.profile.firstName ?? ""} ${
                  item.insured.profile.lastName ?? ""
                }`.trim();
                return (
                  <div key={item.insured.id} className="flex flex-col items-center">
                    {item.confirmed === true ? (
                      <>
                        <SignedPill name={name} />
                        {item.confirmedDate && (
                          <Timestamp value={formatSignedAt(item.confirmedDate)} />
                        )}
                      </>
                    ) : item.confirmed === false ? (
                      <>
                        <PendingPill name={name} showArrows />
                        {item.confirmedDate && (
                          <Timestamp value={formatSignedAt(item.confirmedDate)} />
                        )}
                      </>
                    ) : (
                      <PendingPill name={name} />
                    )}
                  </div>
                );
              })}

              {!sigData?.insureds?.length && <p className="text-[12px] text-gray-400">—</p>}
            </div>
          </SignerColumn>
        </div>
      )}
    </div>
  );
}

export default Signature;
